export const ESSAY_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';
export const ESSAY_PDF_ACCEPT = 'application/pdf,.pdf';
export const ESSAY_ATTACHMENT_ACCEPT = `${ESSAY_IMAGE_ACCEPT},${ESSAY_PDF_ACCEPT}`;
export const ESSAY_IMAGE_SOURCE_MAX_BYTES = 20 * 1024 * 1024;
export const ESSAY_IMAGE_UPLOAD_MAX_BYTES = 6 * 1024 * 1024;
export const ESSAY_PDF_MAX_BYTES = 10 * 1024 * 1024;
export const ESSAY_ATTACHMENTS_TOTAL_MAX_BYTES = 10 * 1024 * 1024;

const MAX_IMAGE_EDGE = 2200;
const COMPRESS_FROM_BYTES = 4 * 1024 * 1024;

function getBasicFileError(file) {
  if (!file) return '첨부 파일을 선택해 주세요.';
  if (file.size <= 0) return '내용이 없는 파일입니다.';
  if (file.size > ESSAY_IMAGE_SOURCE_MAX_BYTES) return '첨부 파일은 한 개당 20MB 이하만 선택할 수 있습니다.';
  return '';
}

async function detectAttachmentMimeType(file) {
  const buffer = await file.slice(0, 1024).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (
    bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
    && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
  ) return 'image/png';

  const ascii = String.fromCharCode(...bytes);
  if (ascii.slice(0, 4) === 'RIFF' && ascii.slice(8, 12) === 'WEBP') return 'image/webp';
  if (/^%PDF-(?:1\.[0-7]|2\.0)/.test(ascii)) return 'application/pdf';
  return '';
}

function normalizeFileMimeType(file, mimeType) {
  if (file.type === mimeType) return file;
  return new File([file], file.name, { type: mimeType, lastModified: file.lastModified || Date.now() });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('이미지를 읽을 수 없습니다. 다른 파일을 선택해 주세요.'));
    };
    image.src = objectUrl;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('이미지 크기를 줄이지 못했습니다.'));
    }, 'image/jpeg', quality);
  });
}

function buildCompressedName(fileName) {
  const baseName = String(fileName || 'essay-image').replace(/\.[^.]+$/, '') || 'essay-image';
  return `${baseName}.jpg`;
}

export async function prepareEssayAttachment(file, options = {}) {
  const validationError = getBasicFileError(file);
  if (validationError) throw new Error(validationError);

  const configuredMaxImageEdge = Number(options.maxImageEdge);
  const maxImageEdge = Number.isFinite(configuredMaxImageEdge) && configuredMaxImageEdge > 0
    ? Math.min(4000, Math.round(configuredMaxImageEdge))
    : MAX_IMAGE_EDGE;
  const configuredUploadMaxBytes = Number(options.maxUploadBytes);
  const imageUploadMaxBytes = Number.isFinite(configuredUploadMaxBytes) && configuredUploadMaxBytes > 0
    ? Math.min(ESSAY_IMAGE_SOURCE_MAX_BYTES, Math.round(configuredUploadMaxBytes))
    : ESSAY_IMAGE_UPLOAD_MAX_BYTES;

  const detectedMimeType = await detectAttachmentMimeType(file);
  if (!detectedMimeType) throw new Error('JPG, PNG, WEBP 이미지 또는 PDF 파일만 사용할 수 있습니다.');

  if (detectedMimeType === 'application/pdf') {
    if (file.size > ESSAY_PDF_MAX_BYTES) {
      throw new Error('PDF는 한 파일당 10MB 이하만 사용할 수 있습니다.');
    }
    return normalizeFileMimeType(file, detectedMimeType);
  }

  const normalizedImageFile = normalizeFileMimeType(file, detectedMimeType);
  const image = await loadImage(normalizedImageFile);
  const longEdge = Math.max(image.naturalWidth, image.naturalHeight);
  if (longEdge <= maxImageEdge && file.size <= Math.min(COMPRESS_FROM_BYTES, imageUploadMaxBytes)) {
    return normalizedImageFile;
  }

  const scale = Math.min(1, maxImageEdge / longEdge);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('이 브라우저에서는 이미지를 처리할 수 없습니다.');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, 0, 0, width, height);

  let blob = await canvasToBlob(canvas, 0.9);
  if (blob.size > imageUploadMaxBytes) blob = await canvasToBlob(canvas, 0.82);
  if (blob.size > imageUploadMaxBytes) {
    throw new Error(`이미지를 ${Math.round(imageUploadMaxBytes / (1024 * 1024))}MB 이하로 줄이지 못했습니다. 더 작은 이미지로 다시 시도해 주세요.`);
  }

  return new File([blob], buildCompressedName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

export function createEssayAttachmentValue(file) {
  const isPdf = file.type === 'application/pdf';
  return {
    file,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    kind: isPdf ? 'pdf' : 'image',
    previewUrl: isPdf ? '' : URL.createObjectURL(file),
  };
}

export function releaseEssayAttachmentValue(value) {
  if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
}

export function formatAttachmentSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
